import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faArrowLeft,
  faTrashCan,
  faFolderPlus,
  faFolderClosed
} from '@fortawesome/free-solid-svg-icons';

const basename = (key = '') => {
  return key.split('/').pop() || key;
};

const formatBytes = (bytes = 0) => {
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024;
    i++;
  }
  return `${bytes.toFixed(i ? 1 : 0)} ${units[i]}`;
};

export default ({
  busy = {},
  files = [],
  onChangePath,
  onDelete = () => {},
  onCreateFolder = () => {}
}) => {
  const [path, _setPath] = useState([]);
  const [fileToDelete, setFileToDelete] = useState(null);

  // NEW: create-folder modal state
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const setPath = useCallback(
    path => {
      _setPath(path);
      onChangePath && onChangePath(path);
    },
    [onChangePath]
  );

  const entries = useMemo(() => {
    const folders = new Set();
    const visibleFiles = [];

    files.forEach(file => {
      const parts = file.key.split('/').filter(Boolean);
      for (let i = 0; i < path.length; i++) {
        if (parts[i] !== path[i]) return;
      }
      if (parts.length === path.length + 1 && file.key.endsWith('/')) {
        folders.add(parts[path.length]);
      } else if (parts.length === path.length + 1) {
        visibleFiles.push(file);
      } else if (parts[path.length]) {
        folders.add(parts[path.length]);
      }
    });
    return {
      folders: Array.from(folders).sort(),
      files: visibleFiles
    };
  }, [files, path]);

  const goInto = folder => setPath([...path, folder]);
  const goUp = () => setPath(path.slice(0, -1));

  const handleDeleteClick = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
    setFileToDelete(file);
  };

  const confirmDelete = () => {
    if (fileToDelete && onDelete) {
      onDelete(fileToDelete);
    }
    setFileToDelete(null);
  };

  const cancelDelete = () => {
    setFileToDelete(null);
  };

  const handleModalDismiss = e => {
    e.preventDefault();
    e.stopPropagation();
    cancelDelete();
  };

  const stopModalClick = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  // NEW: create folder helpers
  const openCreateFolder = () => {
    setNewFolderName('');
    setIsCreateFolderOpen(true);
  };

  const closeCreateFolder = () => {
    setIsCreateFolderOpen(false);
    setNewFolderName('');
  };

  const confirmCreateFolder = () => {
    const name = (newFolderName || '').trim();

    // Basic sanitization: avoid slashes and empty names
    if (!name || name.includes('/')) return;

    const prefix = path.length ? `${path.join('/')}/` : '';
    const folderKey = `${prefix}${name}/`;

    onCreateFolder && onCreateFolder(folderKey);
    closeCreateFolder();
  };

  const handleCreateFolderBackdrop = e => {
    e.preventDefault();
    e.stopPropagation();
    closeCreateFolder();
  };

  return (
    <div className='card shadow-sm'>
      <div className='card-header bg-white d-flex align-items-center gap-2'>
        <button
          className='btn btn-outline-secondary btn-sm'
          onClick={goUp}
          disabled={path.length === 0}
        >
          <FontAwesomeIcon icon={faArrowLeft} className='me-1' />
          Back
        </button>

        {/* NEW: Create folder button */}
        <button
          className='btn btn-outline-primary btn-sm'
          onClick={openCreateFolder}
        >
          <FontAwesomeIcon icon={faFolderPlus} className='me-1' />
          New folder
        </button>

        <div className='text-muted small text-truncate ms-auto'>
          /{path.join('/')}
        </div>
      </div>

      <div className='table-responsive' style={{ position: 'relative' }}>
        <table className='table table-hover align-middle mb-0'>
          <thead className='table-light'>
            <tr>
              <th>Name</th>
              <th style={{ width: 140 }}>Size</th>
              <th style={{ width: 220 }}>Created</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>

          <tbody style={busy.files ? { pointerEvents: 'none' } : undefined}>
            {entries.folders.map(folder => (
              <tr
                key={`folder-${folder}`}
                role='button'
                onClick={() => goInto(folder)}
              >
                <td>
                  <div className='d-flex align-items-center gap-2'>
                    <FontAwesomeIcon icon={faFolder} className='text-warning' />
                    <span className='fw-medium'>{folder}</span>
                  </div>
                </td>
                <td className='text-muted'>—</td>
                <td className='text-muted'>—</td>
                <td className='text-end'>
                  <button
                    className='btn btn-sm btn-link text-danger p-0'
                    title='Delete folder'
                    onClick={e => handleDeleteClick(e, { key: path.join('/') + '/' + folder, is_folder: true })}
                    disabled={!onDelete}
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </td>
              </tr>
            ))}

            {entries.files.map(file => (
              <tr key={file.key}>
                <td>
                  <div className='d-flex align-items-center gap-2'>
                    <FontAwesomeIcon icon={faFile} className='text-secondary' />
                    {file.key_url ? (
                      <a
                        href={file.key_url}
                        download
                        target='_blank'
                        rel='noopener noreferrer'
                        className='fw-medium text-decoration-none'
                        onClick={e => e.stopPropagation()}
                      >
                        {basename(file.key)}
                      </a>
                    ) : (
                      <span className='fw-medium text-muted'>
                        {basename(file.key)}
                      </span>
                    )}
                  </div>
                </td>

                <td className='text-muted'>{formatBytes(file.size)}</td>

                <td className='text-muted'>
                  {new Date(file.created_at).toLocaleString()}
                </td>

                <td className='text-end'>
                  <button
                    className='btn btn-sm btn-link text-danger p-0'
                    title='Delete file'
                    onClick={e => handleDeleteClick(e, file)}
                    disabled={!onDelete}
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </td>
              </tr>
            ))}

            {entries.folders.length === 0 && entries.files.length === 0 && (
              <tr>
                <td colSpan={4} className='text-center text-muted py-4'>
                  Empty
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {busy.files && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.15)',
              top: '35px',
              zIndex: 2
            }}
            aria-label='Loading files'
          >
            <div className='spinner-border text-primary' role='status' aria-hidden='true' />
            <span className='visually-hidden'>Loading...</span>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {fileToDelete && (
        <div
          className='modal fade show d-block'
          tabIndex='-1'
          role='dialog'
          aria-modal='true'
          onClick={handleModalDismiss}
        >
          <div
            className='modal-dialog modal-dialog-centered'
            onClick={stopModalClick}
          >
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Delete {fileToDelete.is_folder ? 'folder' : 'file'}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={cancelDelete}
                />
              </div>

              <div className='modal-body'>
                <p className='mb-0'>
                  Are you sure you want to delete{' '}
                  <strong>{basename(fileToDelete.key)}</strong>?
                </p>
              </div>

              <div className='modal-footer'>
                <button className='btn btn-danger' onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {fileToDelete && <div className='modal-backdrop fade show' />}

      {/* NEW: Create folder modal */}
      {isCreateFolderOpen && (
        <>
          <div
            className='modal fade show d-block'
            tabIndex='-1'
            role='dialog'
            aria-modal='true'
            onClick={handleCreateFolderBackdrop}
          >
            <div
              className='modal-dialog modal-dialog-centered'
              onClick={stopModalClick}
            >
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Create folder</h5>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={closeCreateFolder}
                  />
                </div>

                <div className='modal-body'>
                  <label className='form-label'>Folder name</label>
                  <input
                    className='form-control'
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    placeholder='e.g. invoices'
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') confirmCreateFolder();
                      if (e.key === 'Escape') closeCreateFolder();
                    }}
                  />
                  <div className='form-text'>
                    Will create: <code>/{path.join('/')}{path.length ? '/' : ''}{(newFolderName || '').trim() || '...'}/</code>
                  </div>
                </div>

                <div className='modal-footer'>
                  <button className='btn btn-outline-secondary' onClick={closeCreateFolder}>
                    Cancel
                  </button>
                  <button
                    className='btn btn-primary'
                    onClick={confirmCreateFolder}
                    disabled={!newFolderName.trim() || newFolderName.includes('/')}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show' />
        </>
      )}
    </div>
  );
};
